// Centralized fetch wrapper to enforce session credentials, department/user headers,
// automatic CSRF for mutating requests, and unified 401 handling.
// Usage: import { apiFetch } from './apiFetch';
// const res = await apiFetch('/categories?page=1');

import { getCsrfToken, invalidateCsrfToken } from './csrf';
import { getCurrentUser } from './auth';

// Simple pub/sub for auth events (avoid pulling in external libs)
const listeners = { unauth: [] };
export function onUnauthorized(cb) { listeners.unauth.push(cb); return () => { listeners.unauth = listeners.unauth.filter(f => f!==cb); }; }
function emitUnauthorized(detail) { listeners.unauth.forEach(fn => { try { fn(detail); } catch(_) {} }); }

const MUTATING = new Set(['POST','PUT','PATCH','DELETE']);

let recheckingSession = false;
async function recheckSessionOnce() {
  if (recheckingSession) return false;
  recheckingSession = true;
  try {
    const r = await fetch('/api/auth/session', { credentials: 'include' });
    if (!r.ok) return false;
    const js = await r.json().catch(()=>({}));
    return !!js.authenticated;
  } catch (_) {
    return false;
  } finally {
    recheckingSession = false;
  }
}

export async function apiFetch(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const user = getCurrentUser();
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = { ...(options.headers || {}) };
  // Only set JSON content-type if not provided and body is not FormData
  if (!headers['Content-Type'] && !isForm) {
    headers['Content-Type'] = 'application/json';
  }

  // Normalize department header priority
  if (user?.bahagian) {
    if (!headers['X-User-Department']) headers['X-User-Department'] = user.bahagian;
    if (!headers['x-bahagian-pemantau']) headers['x-bahagian-pemantau'] = user.bahagian;
  }
  if (user?.id_pengguna && !headers['x-id-pengguna']) {
    headers['x-id-pengguna'] = String(user.id_pengguna).toLowerCase();
  }

  // Add CSRF token for mutating requests if not already present
  if (MUTATING.has(method) && !headers['X-CSRF-Token']) {
    try {
      const token = await getCsrfToken();
      headers['X-CSRF-Token'] = token;
    } catch (e) {
      console.warn('[apiFetch] Failed to obtain CSRF token:', e.message);
    }
  }

  let resp = await fetch(path, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  // If CSRF is invalid/stale, refresh once and retry
  if (resp.status === 403 && MUTATING.has(method)) {
    try {
      invalidateCsrfToken();
      const fresh = await getCsrfToken();
      const retryHeaders = { ...headers, 'X-CSRF-Token': fresh };
      resp = await fetch(path, { ...options, method, headers: retryHeaders, credentials: 'include' });
    } catch (_) {
      // fallthrough to normal handling
    }
  }

  // Treat 440 (login time-out) similar to 401
  if (resp.status === 440) {
    console.warn('[apiFetch] 440 Login Time-out for', path);
    const stillAuth = await recheckSessionOnce();
    if (!stillAuth) {
      try { localStorage.removeItem('user'); localStorage.removeItem('token'); } catch(_){ }
      emitUnauthorized({ path, status: 440 });
      if (!listeners.unauth.length) {
        if (typeof window !== 'undefined' && !/\/login/i.test(window.location.pathname)) {
          try { window.location.href = '/authentication/sign-in'; } catch(_) {}
        }
      }
    }
  }

  if (resp.status === 401) {
    console.warn('[apiFetch] 401 Unauthorized for', path);
    // Attempt a lightweight session recheck (guards against race after login or session regen)
    const stillAuth = await recheckSessionOnce();
    if (!stillAuth) {
      // Clear stale client cache
      try { localStorage.removeItem('user'); localStorage.removeItem('token'); } catch(_){ }
      emitUnauthorized({ path });
      // Optional immediate redirect (defer to listeners if present)
      if (!listeners.unauth.length) {
        // Avoid redirect loop if already on login
        if (typeof window !== 'undefined' && !/\/login/i.test(window.location.pathname)) {
          console.info('[apiFetch] Redirecting to /authentication/sign-in after 401');
          try { window.location.href = '/authentication/sign-in'; } catch(_) {}
        }
      }
    }
  }
  return resp;
}

export async function apiJson(path, options = {}) {
  const res = await apiFetch(path, options);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status} ${res.statusText} - ${text}`);
  }
  try { return await res.json(); } catch (_) { return null; }
}
